import { encodeQueryValue } from './encodeQueryValue';
import { lookupInventoryCount } from './inventoryKey';

const VALID_LINK_PARAMETERS = ['year', 'make', 'model', 'gvBodyStyle', 'trim'];

const hasInventory = (vehicle, inventoryCounts) => {
	return (
		lookupInventoryCount(inventoryCounts, vehicle?.model, vehicle?.gvBodyStyle) >
		0
	);
};

const shouldUseFallbackUrl = (
	noInventoryFallbackUrl,
	vehicle,
	inventoryCounts
) => {
	const hasFallbackUrl =
		noInventoryFallbackUrl && noInventoryFallbackUrl.length > 0;
	return hasFallbackUrl && !hasInventory(vehicle, inventoryCounts);
};

const buildFallbackUrl = (template, vehicle) => {
	return template.replace(/\$\{(\w+)\}/g, (_, key) => {
		const value = vehicle[key];
		return value != null ? encodeURIComponent(value) : '';
	});
};

const getVehicleParameterValue = (paramName, vehicle) => {
	if (paramName === 'model') {
		return vehicle?.modelInv?.toString().trim();
	}
	return vehicle[paramName]?.toString();
};

const encodeParameterValue = (value) => {
	const encodedValue = encodeQueryValue(value);
	return encodedValue?.replace(/ /g, '+');
};

const buildQueryParameter = (paramName, vehicle) => {
	const value = getVehicleParameterValue(paramName, vehicle);
	if (!value) {
		return null;
	}
	const encodedValue = encodeParameterValue(value);
	return `${paramName}=${encodedValue}`;
};

const buildStandardParameters = (parametersString, vehicle) => {
	const params = parametersString.split(',');
	const result = [];

	// eslint-disable-next-line no-restricted-syntax
	for (const param of params) {
		if (VALID_LINK_PARAMETERS.includes(param)) {
			const queryParam = buildQueryParameter(param, vehicle);
			if (queryParam) {
				result.push(queryParam);
			}
		}
	}

	return result;
};

const buildSrpParameters = (vehicle) => {
	if (!vehicle.srpParams || Object.keys(vehicle.srpParams).length === 0) {
		return [];
	}
	return Object.entries(vehicle.srpParams).flatMap(([key, listOfValues]) => {
		if (!listOfValues || listOfValues.length === 0) {
			return [];
		}
		// Ensure listOfValues is an array
		const valuesArray = Array.isArray(listOfValues)
			? listOfValues
			: [listOfValues];
		return valuesArray.map((value) => {
			const encodedKey = encodeParameterValue(key);
			const encodedValue = encodeParameterValue(value);
			return `${encodedKey}=${encodedValue}`;
		});
	});
};

const buildQueryString = (parameters) => {
	return parameters.join('&');
};

const buildStandardModelLink = (
	modelLinkPath,
	modelLinkParameters,
	vehicle
) => {
	const standardParams = buildStandardParameters(
		modelLinkParameters,
		vehicle
	);
	const srpParams = buildSrpParameters(vehicle);
	const allParameters = [...standardParams, ...srpParams];
	const queryString = buildQueryString(allParameters);
	return `${modelLinkPath}?${queryString}`;
};

/**
 * Generates a model link URL for a vehicle, either using standard parameters or a fallback URL.
 * @param {string} modelLinkPath - The base path for the model link
 * @param {string} modelLinkParameters - Comma-separated list of parameters to include
 * @param {Object} vehicle - The vehicle object containing data for the link
 * @param {string} noInventoryFallbackUrl - Template URL to use when vehicle has no inventory (empty string to skip)
 * @param {Object} inventoryCounts - Dictionary of inventory counts by model key
 * @returns {string} The generated model link URL
 */
export const getModelLink = (
	modelLinkPath,
	modelLinkParameters,
	vehicle,
	noInventoryFallbackUrl = '',
	inventoryCounts = {}
) => {
	if (
		shouldUseFallbackUrl(noInventoryFallbackUrl, vehicle, inventoryCounts)
	) {
		return buildFallbackUrl(noInventoryFallbackUrl, vehicle);
	}

	return buildStandardModelLink(modelLinkPath, modelLinkParameters, vehicle);
};
